/* eslint-disable react-refresh/only-export-components -- domain provider and its consumer hook */
import { createContext, useContext, useRef, useState, type ReactNode } from "react";
import { useApolloClient } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { usePendingScope } from "@/lib/pending-edits";
import { LocalizedError, localizedMessageOf } from "@/lib/localized-error";
import { userErrorMessage } from "@/lib/user-error";
import { CreateOrganizationalRecordCommentDocument, UpdateOrganizationalRecordCommentDocument,
  CreateOrganizationalRecordEntryDocument, UpdateOrganizationalRecordEntryDocument, OrganizationalRecordEntryKind } from "@/graphql/generated/operations";
import { linkInputs, type OrganizationalRecordReference } from "./organizational-record-link-types";

export interface OrganizationalRecordDraftValue {
  type: "entry" | "comment";
  id?: string;
  entryId?: string;
  title?: string;
  kind?: OrganizationalRecordEntryKind;
  content: string;
  links: readonly OrganizationalRecordReference[];
}
export interface OrganizationalRecordDraft extends OrganizationalRecordDraftValue { baseline: string }
const fingerprint = (value: OrganizationalRecordDraftValue) => JSON.stringify([
  value.title ?? "", value.kind ?? "", value.content,
  value.links.map(link => `${link.kind}:${link.id}`).sort(),
]);
export const isOrganizationalRecordDraftDirty = (draft: OrganizationalRecordDraft) => fingerprint(draft) !== draft.baseline;
export const commentDraftKey = (entryId?: string, id?: string) => `comment:${id ?? `new:${entryId ?? "general"}`}`;

interface DraftContext {
  drafts: Readonly<Record<string, OrganizationalRecordDraft>>;
  busy: boolean;
  errors: Readonly<Record<string, string>>;
  update: (key: string, initial: OrganizationalRecordDraftValue, patch?: Partial<OrganizationalRecordDraftValue>) => void;
  discard: (key: string) => void;
}
const Context = createContext<DraftContext | null>(null);

// Lives at the authorised organizationalRecord boundary, never in storage or Apollo's shared cache.
// Forms can unmount while the one page-level SaveBar still owns their complete drafts.
export function OrganizationalRecordDraftsProvider({ recordId, canWrite, children }: {
  recordId: string; canWrite: boolean; children: ReactNode;
}) {
  const client = useApolloClient();
  const intl = useIntl();
  const [drafts, setDrafts] = useState<Record<string, OrganizationalRecordDraft>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const flight = useRef<Promise<void> | null>(null);

  function discard(key: string) {
    setDrafts(previous => { const next = { ...previous }; delete next[key]; return next; });
    setErrors(previous => { const next = { ...previous }; delete next[key]; return next; });
  }
  function update(key: string, initial: OrganizationalRecordDraftValue, patch: Partial<OrganizationalRecordDraftValue> = {}) {
    if (!canWrite || flight.current) return;
    setDrafts(previous => ({ ...previous, [key]: { ...(previous[key] ?? { ...initial, baseline: fingerprint(initial) }), ...patch } }));
    setErrors(previous => { const next = { ...previous }; delete next[key]; return next; });
  }
  async function write(draft: OrganizationalRecordDraft) {
    const content = draft.content.trim();
    const contentLimit = draft.type === "entry" ? 65535 : 4096;
    if (!content || Array.from(content).length > contentLimit || (draft.type === "entry" &&
      (!draft.title?.trim() || Array.from(draft.title.trim()).length > 255))) {
      throw new LocalizedError(intl.formatMessage({ id: "organizationalRecord.drafts.invalid" }));
    }
    const links = linkInputs(draft.links);
    if (draft.type === "entry") {
      const input = { title: draft.title!.trim(), content, kind: draft.kind ?? OrganizationalRecordEntryKind.Note, links };
      if (draft.id) await client.mutate({ mutation: UpdateOrganizationalRecordEntryDocument, variables: { id: draft.id, input }, fetchPolicy: "no-cache" });
      else await client.mutate({ mutation: CreateOrganizationalRecordEntryDocument, variables: { input: { ...input, recordId } }, fetchPolicy: "no-cache" });
    } else if (draft.id) {
      await client.mutate({ mutation: UpdateOrganizationalRecordCommentDocument, variables: { id: draft.id, content, links }, fetchPolicy: "no-cache" });
    } else {
      await client.mutate({ mutation: CreateOrganizationalRecordCommentDocument, variables: { input: { data: {
        organizationalRecord: { id: recordId }, entry: draft.entryId ? { id: draft.entryId } : null, content, links,
      } } }, fetchPolicy: "no-cache" });
    }
  }
  function save() {
    if (flight.current) return flight.current;
    const pending = Object.entries(drafts).filter(([, draft]) => isOrganizationalRecordDraftDirty(draft));
    const run = async () => {
      if (!canWrite) throw new LocalizedError(intl.formatMessage({ id: "common.saveFailed" }));
      setBusy(true);
      try {
        const results = await Promise.allSettled(pending.map(async ([key, draft]) => {
          await write(draft);
          discard(key);
        }));
        const failures: Record<string, string> = {};
        results.forEach((result, index) => {
          if (result.status === "rejected") failures[pending[index]![0]] = localizedMessageOf(result.reason) ?? userErrorMessage(intl, result.reason);
        });
        setErrors(failures);
        if (results.some(result => result.status === "fulfilled")) {
          // Read failures remain query errors, never turn a committed create into a retry.
          await client.refetchQueries({ include: "active", onQueryUpdated: query =>
            ["OrganizationalRecordEntries", "OrganizationalRecordComments", "OrganizationalRecordLinkedEntry", "OrganizationalRecordLinkedComment", "OrganizationalRecordBacklinks"]
              .includes(query.queryName ?? "") ? query.refetch() : false,
          }).catch(() => undefined);
        }
        if (Object.keys(failures).length) throw new LocalizedError(Object.values(failures)[0]!);
      } finally { setBusy(false); }
    };
    flight.current = Promise.resolve().then(run).finally(() => { flight.current = null; });
    return flight.current;
  }
  usePendingScope(`organizational-record-drafts:${recordId}`, {
    dirty: canWrite && Object.values(drafts).some(isOrganizationalRecordDraftDirty),
    save,
    discard: () => { if (!flight.current) { setDrafts({}); setErrors({}); } },
  });
  return <Context.Provider value={{ drafts, busy, errors, update, discard }}>{children}</Context.Provider>;
}

export function useOrganizationalRecordDrafts() {
  const context = useContext(Context);
  if (!context) throw new Error("OrganizationalRecord forms require their authorised draft provider");
  return context;
}
